import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Mail, Send, Loader2, X, Plus, FileCheck2, Image as ImageIcon, Users } from 'lucide-react';
import { buildScopePack } from '../../lib/factsheetPdf';
import { getCurrencySymbol } from '../../lib/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

async function fetchLogoDataUri(orgId) {
  if (!orgId) return null;
  try {
    const res = await axios.get(`${API_URL}/api/organizations/${orgId}/logo`, {
      withCredentials: true, responseType: 'blob',
    });
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(res.data);
    });
  } catch { return null; }
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * "Email Scope Pack" dialog — generates the branded PDF client-side and
 * hands it to /api/share/email for Resend-backed delivery. Pre-fills recipients
 * from the Customer Organization's Stakeholder list.
 */
export function EmailScopePackDialog({ open, onOpenChange, estimate, components, preparedBy, onSent }) {
  const orgName = estimate?.organization_name || estimate?.org_name || 'Client';
  const implName = estimate?.implementation_name || estimate?.impl_name || '';

  const [recipients, setRecipients] = useState([]); // [{email, name?, role?}]
  const [newEmail, setNewEmail] = useState('');
  const [stakeholdersLoaded, setStakeholdersLoaded] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [onlyRequired, setOnlyRequired] = useState(false);
  const [coBrand, setCoBrand] = useState(true);
  const [customNote, setCustomNote] = useState('');
  const [sending, setSending] = useState(false);

  // Prefill on open
  useEffect(() => {
    if (!open) return;
    setSubject(`Scope Pack \u2014 ${orgName}${implName ? ` (${implName})` : ''}`);
    const senderName = preparedBy?.name || 'your Seertech team';
    const body = `Hi,

Please find attached the latest Scope Pack for the ${orgName} engagement${implName ? ` \u2014 ${implName}` : ''}. It covers every component in the proposed implementation, with factsheets and effort ranges for each.

Happy to walk through anything on a call. Any scope changes after this will be tracked as a Change Request.

Thanks,
${senderName}`;
    setMessage(body);
    setStakeholdersLoaded(false);
    setRecipients([]);
    setNewEmail('');
    setOnlyRequired(false);
    setCoBrand(true);
    setCustomNote('');
  }, [open, orgName, implName, preparedBy]);

  // Load stakeholders for the org when dialog opens
  useEffect(() => {
    if (!open || stakeholdersLoaded || !estimate?.org_id) return;
    axios.get(`${API_URL}/api/organizations/${estimate.org_id}/stakeholders`, { withCredentials: true })
      .then(res => {
        const list = (res.data?.stakeholders || [])
          .filter(s => s?.email && isValidEmail(s.email))
          .map(s => ({ email: s.email, name: s.name || '', role: s.role || '' }));
        setRecipients(list);
      })
      .catch(() => { /* silent */ })
      .finally(() => setStakeholdersLoaded(true));
  }, [open, stakeholdersLoaded, estimate?.org_id]);

  const addEmail = useCallback(() => {
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) { toast.error('Not a valid email'); return; }
    if (recipients.some(r => r.email.toLowerCase() === trimmed)) { toast.info('Already added'); setNewEmail(''); return; }
    setRecipients(prev => [...prev, { email: trimmed, name: '', role: '' }]);
    setNewEmail('');
  }, [newEmail, recipients]);

  const removeRecipient = (email) => setRecipients(prev => prev.filter(r => r.email !== email));

  const handleSend = async () => {
    if (recipients.length === 0) { toast.error('Add at least one recipient'); return; }
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    if (!components || components.length === 0) { toast.error('No components to send'); return; }

    setSending(true);
    try {
      let clientLogoDataUri = null;
      if (coBrand && estimate?.org_id) {
        clientLogoDataUri = await fetchLogoDataUri(estimate.org_id);
      }

      // Generate one send_id per recipient up-front so the PDF footer can
      // embed a per-recipient feedback link that ties back to the send record.
      const origin = (typeof window !== 'undefined' && window.location?.origin) || '';
      const perRecipient = recipients.map(r => ({
        ...r,
        send_id: (crypto?.randomUUID && crypto.randomUUID()) ||
                 `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
      }));

      // Send to each recipient (backend records each send in db.scope_pack_sends)
      const results = await Promise.allSettled(perRecipient.map(r => {
        const feedbackUrl = origin ? `${origin}/feedback/${r.send_id}` : null;
        const { doc, filename } = buildScopePack({
          organizationName: orgName,
          implementationName: implName,
          implementationType: estimate?.implementation_type,
          complexity: estimate?.complexity,
          totalEffort: estimate?.results?.total_effort,
          totalCost: estimate?.results?.total_cost,
          currencySymbol: getCurrencySymbol(estimate?.currency_code),
          preparedBy,
        }, components, {
          onlyRequired,
          includeCoverStats: true,
          includePreparedBy: true,
          customNote: customNote.trim(),
          clientLogoDataUri,
          feedbackUrl,
        });
        const dataUri = doc.output('datauristring');
        const pdfBase64 = dataUri.split(',')[1];
        return axios.post(`${API_URL}/api/scope-pack/send`, {
          send_id: r.send_id,
          estimate_id: estimate?.id || estimate?._id || null,
          estimate_name: estimate?.estimate_name || `${orgName}${implName ? ` \u2014 ${implName}` : ''}`,
          org_id: estimate?.org_id || null,
          recipient_email: r.email,
          recipient_name: r.name || '',
          subject: subject.trim(),
          message: message.trim(),
          pdf_base64: pdfBase64,
          filename,
        }, { withCredentials: true });
      }));

      const ok = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.length - ok;
      if (ok > 0 && failed === 0) {
        toast.success(`Scope Pack sent to ${ok} recipient${ok === 1 ? '' : 's'}`);
        if (onSent) onSent();
        onOpenChange(false);
      } else if (ok > 0 && failed > 0) {
        toast.warning(`Sent to ${ok}, failed ${failed}. Check email addresses and try again.`);
        if (onSent) onSent();
      } else {
        toast.error('Failed to send. Please check the recipient addresses.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="email-scope-pack-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0F2040]">
            <Mail className="w-4 h-4" /> Email Scope Pack
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Generates the branded PDF and delivers it via Seertech&apos;s email service.
            Recipients are pre-filled from the Customer Organization&apos;s Stakeholder list.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Recipients */}
          <div>
            <Label className="text-xs font-medium text-[#0F2040] flex items-center gap-1">
              <Users className="w-3 h-3" /> Recipients
            </Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="email-recipient-chips">
              {recipients.length === 0 && (
                <span className="text-[11px] text-slate-400 italic">
                  {stakeholdersLoaded ? 'No stakeholders on file — add one below.' : 'Loading stakeholders\u2026'}
                </span>
              )}
              {recipients.map(r => (
                <Badge key={r.email} variant="secondary"
                  className="pl-2 pr-1 py-0.5 text-[11px] bg-slate-100 text-slate-700 border border-slate-200 gap-1">
                  <span>{r.name ? `${r.name} <${r.email}>` : r.email}</span>
                  <button type="button" onClick={() => removeRecipient(r.email)}
                    className="hover:bg-slate-200 rounded-sm p-0.5"
                    data-testid={`email-recipient-remove-${r.email}`}
                    aria-label={`Remove ${r.email}`}>
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <Input
                type="email" placeholder="Add another email..." value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
                className="text-xs h-8"
                data-testid="email-new-recipient-input"
              />
              <Button type="button" size="sm" variant="outline" onClick={addEmail} className="h-8"
                data-testid="email-add-recipient-btn">
                <Plus className="w-3 h-3" />
              </Button>
            </div>
          </div>

          {/* Subject */}
          <div>
            <Label className="text-xs font-medium text-[#0F2040]">Subject</Label>
            <Input value={subject} onChange={e => setSubject(e.target.value)}
              className="text-xs mt-1 h-8" data-testid="email-subject-input" />
          </div>

          {/* Message */}
          <div>
            <Label className="text-xs font-medium text-[#0F2040]">Message</Label>
            <Textarea value={message} onChange={e => setMessage(e.target.value)} rows={6}
              className="text-xs mt-1" data-testid="email-message-input" />
          </div>

          {/* PDF options (compact) */}
          <div className="border-t border-slate-200 pt-3 space-y-2">
            <div className="text-xs font-medium text-[#0F2040] flex items-center gap-1">
              <FileCheck2 className="w-3 h-3" /> PDF options
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={onlyRequired} onChange={e => setOnlyRequired(e.target.checked)}
                className="mt-0.5" data-testid="email-opt-only-required" />
              <div>
                <div className="text-[11px] text-slate-700">Required components only</div>
              </div>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={coBrand} onChange={e => setCoBrand(e.target.checked)}
                className="mt-0.5" data-testid="email-opt-cobrand" />
              <div>
                <div className="text-[11px] text-slate-700 flex items-center gap-1">
                  <ImageIcon className="w-3 h-3" /> Co-brand with client logo
                </div>
              </div>
            </label>
            <div>
              <Label className="text-[10px] font-medium text-slate-500">Custom cover note (optional)</Label>
              <Textarea value={customNote} onChange={e => setCustomNote(e.target.value)}
                placeholder="Shown on the PDF cover page." rows={2} className="text-xs mt-1" maxLength={400}
                data-testid="email-pdf-note" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}
            data-testid="email-cancel-btn">Cancel</Button>
          <Button size="sm" onClick={handleSend} disabled={sending || recipients.length === 0}
            className="bg-[#0F2040] hover:bg-[#1A3055] text-white"
            data-testid="email-send-btn">
            {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                     : <Send className="w-3.5 h-3.5 mr-1.5" />}
            Send to {recipients.length || 0} recipient{recipients.length === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
